import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Badge, Button, Input, Spinner, Table } from "reactstrap";
import { useDemoCore } from "../../../cores/demo";
import { useRouterCore } from "../../../cores/router";
import {
  DemoRequest,
  DemoStatus,
} from "../../../cores/demo/interfaces/DemoRequest";
import "../demo/DemoRequest.scss";

const whatsappUrl = (phone: string) => {
  const digits = String(phone || "").replace(/\D/g, "").replace(/^00/, "");
  return digits ? `https://wa.me/${digits}` : "";
};

export function WembiiAdminDemoRequestsSection() {
  const intl = useIntl();
  const { getDemoRequests, updateDemoRequestStatus } = useDemoCore();
  const { goToUserProfile } = useRouterCore();
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDemoRequests();
      setRequests(data);
    } finally {
      setLoading(false);
    }
  }, [getDemoRequests]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatusChange = useCallback(
    async (id: number, status: DemoStatus) => {
      setUpdatingId(id);
      try {
        const updated = await updateDemoRequestStatus(id, status);
        if (updated) {
          setRequests((prev) =>
            prev.map((r) => (r.id === id ? { ...r, status } : r))
          );
        }
      } finally {
        setUpdatingId(null);
      }
    },
    [updateDemoRequestStatus]
  );

  const channelLabel = (channel: string) =>
    intl.formatMessage({ id: `app.demo.channel.${channel}` });
  const formatDateTime = (value: string) => {
    const date = moment(value);
    return date.isValid() ? date.format("DD MMM YYYY · HH:mm") : value || "—";
  };

  return (
    <section className="demo-admin-simple mb-5">
      <div className="d-flex align-items-start justify-content-between mb-2">
        <h5 className="wembii-admin-section-title mb-0">
          {intl.formatMessage({ id: "app.demo.admin.title" })}
          {requests.length > 0 ? (
            <Badge color="primary" pill className="ml-2 align-middle">
              {requests.length}
            </Badge>
          ) : null}
        </h5>
        <Button
          color="link"
          size="sm"
          className="demo-admin-simple__refresh"
          onClick={load}
          disabled={loading}
          title={intl.formatMessage({ id: "app.demo.admin.refresh" })}
        >
          <FontAwesomeIcon icon={faSync} spin={loading} />
        </Button>
      </div>
      <p className="small text-muted mb-3">
        {intl.formatMessage({ id: "app.demo.admin.intro" })}
      </p>

      {loading && requests.length === 0 ? (
        <div className="small text-muted py-4 text-center">
          <Spinner color="primary" />
        </div>
      ) : requests.length === 0 ? (
        <p className="small text-muted mb-0">
          {intl.formatMessage({ id: "app.demo.admin.empty" })}
        </p>
      ) : (
        <div className="table-responsive wembii-admin-table-wrap">
          <Table size="sm" className="wembii-admin-table demo-admin-simple__table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.date" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.name" })}</th>
                <th>{intl.formatMessage({ id: "app.emailTxt" })}</th>
                <th>{intl.formatMessage({ id: "app.phoneTxt" })}</th>
                <th>
                  {intl.formatMessage({ id: "app.demo.form.preferredAt" })}
                </th>
                <th>{intl.formatMessage({ id: "app.demo.form.channel" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.form.message" })}</th>
                <th>{intl.formatMessage({ id: "app.demo.admin.col.status" })}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="small">{formatDateTime(r.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="demo-admin-simple__person font-weight-medium"
                      onClick={() => goToUserProfile(r.email)}
                      title={intl.formatMessage(
                        { id: "app.demo.admin.viewUser" },
                        { name: r.name }
                      )}
                    >
                      {r.name}
                    </button>
                  </td>
                  <td className="small">
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                  </td>
                  <td>
                    {whatsappUrl(r.phone) ? (
                      <a
                        className="demo-admin-simple__whatsapp"
                        href={whatsappUrl(r.phone)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${intl.formatMessage({
                          id: "app.demo.admin.openWhatsapp",
                        })}: ${r.phone}`}
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                        <span>{r.phone}</span>
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="small">
                    {formatDateTime(r.preferredAt)}
                  </td>
                  <td className="small">{channelLabel(r.channel)}</td>
                  <td className="small">
                    <span title={r.message}>{r.message || "—"}</span>
                  </td>
                  <td>
                    <Input
                      type="select"
                      bsSize="sm"
                      className={`demo-admin__status-select demo-admin__status-select--${r.status}`}
                      value={r.status}
                      disabled={updatingId === r.id}
                      onChange={(e) =>
                        onStatusChange(r.id, e.target.value as DemoStatus)
                      }
                    >
                      <option value="pending">
                        {intl.formatMessage({ id: "app.demo.status.pending" })}
                      </option>
                      <option value="scheduled">
                        {intl.formatMessage({ id: "app.demo.status.scheduled" })}
                      </option>
                      <option value="done">
                        {intl.formatMessage({ id: "app.demo.status.done" })}
                      </option>
                      <option value="cancelled">
                        {intl.formatMessage({ id: "app.demo.status.cancelled" })}
                      </option>
                    </Input>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  );
}
